import ko from "knockout";
import _ from "lodash";
import apiCore from "services/apiCore";
import uploadService from "services/fileUploadService";
import localLogger from "services/localLogger";
import serverLogger from "services/serverLogger";
import { getPcAuthToken } from "util/pcAuthToken";
import { sessionService } from "services/sessionService";
import {
    selectUser,
    selectStatus,
    selectAuthorization,
} from "features/auth/currentUserSlice";

import authClientService from "services/authClientService";

const principalId = ko.observable(null);
const oAuthError = ko.observable(false);

let authorizations = [];
let namedPermissions = [];

const PERM_KEY = "perm:";
const ROLE_KEY = "role:";

/**
 * Parses permissions strings based on the passed in key
 * @returns {Array}
 */
function parsePermissions(key, permString) {
    permString = permString.toLowerCase();
    if (permString.indexOf(key) === 0) {
        return permString.split(key)[1];
    }
    return null;
}

/**
 * Returns permissin string for role based permissions
 * @param role
 * @returns {Array}
 */
function getRolePermission(role) {
    return parsePermissions(ROLE_KEY, role);
}

/**
 * Returns permissions key for name based permissions
 * @param permission
 * @returns {Array}
 */
function getNamePermission(permission) {
    return parsePermissions(PERM_KEY, permission);
}

/**
 * Returns an object that contains the permissions themselves and either the role or name based permissions that
 * are associated with them based on the permission prefix.  If there is no permission prefix the name and role permissions
 * are combined and the string is compared to the combined permisions list.
 *
 * If there are no permission prefixes a warn flag will be set in order to show a deprecation warning in the console.
 *
 * @param auth
 * @returns {{auths: Array, permission: (Array|*), warn: boolean}}
 */
function getAuthorizationsAndPermissions(auth) {
    const namePerm = getNamePermission(auth),
        rolePerm = namePerm ? null : getRolePermission(auth),
        auths = namePerm
            ? namePerm
                ? namedPermissions
                : namedPermissions.concat(authorizations)
            : authorizations,
        perm = namePerm || rolePerm || auth,
        warn = !rolePerm && !namePerm;

    return {
        auths: auths,
        permission: perm,
        warn: warn,
    };
}

/**
 * Returns an array of expanded permissions e.g. architect:user:editor,readonly,admin becomes
 * architect:user:editor, architect:user:readonly, and architect:user:admin
 * @param rawPermissions {Array} The array of permissions unexpanded
 * @returns {Array} The array of expanded permissions
 */
function expandPermissionComponents(rawPermissions) {
    const expandedPermissionComponents = [];
    _.forEach(rawPermissions, function (rawPermission) {
        const components = rawPermission.split(",");
        if (components.length > 1) {
            // The first element will contain the root
            const firstPerm = components.shift();
            const lastColonIndex = firstPerm.lastIndexOf(":");
            if (lastColonIndex >= 0) {
                const root = firstPerm.substr(0, lastColonIndex);
                expandedPermissionComponents.push(firstPerm);
                [].push.apply(
                    expandedPermissionComponents,
                    _.map(components, function (component) {
                        return root + ":" + component;
                    })
                );
            } else {
                localLogger.warn(
                    "Invalid component permission format for: " + rawPermission
                );
            }
        } else {
            expandedPermissionComponents.push(rawPermission);
        }
    });

    return expandedPermissionComponents;
}

/**
 * Locally logs a deprecation warning about use of permissions and role prefixes
 * @param permissionString
 */
function logDeprecationMessage(permissionString) {
    const DEPRECATION_MESSAGE =
        "[DEPRECATION WARNING]: The authorization format used is being deprecated.  Please use role:{1} or perm:{1}";
    localLogger.warn(DEPRECATION_MESSAGE.replace(/\{1\}/gi, permissionString));
}

function registerPermissions(authorization) {
    authorizations = authorization.roles
        .filter(({ name }) => name)
        .map(({ name }) => name.toLowerCase());

    namedPermissions = expandPermissionComponents(
        authorization.permissions
    ).map((perm) => perm.toLowerCase());
}

function trimDivisionFromPermission(permission) {
    const permissionPieces = permission.split(":");
    if (permissionPieces.length > 3) {
        return permissionPieces.slice(0, 3).join(":");
    }
    return permission;
}

function registerOauthSession() {
    // Check if we're running standalone (no parent window)
    const isStandalone = window === window.parent;

    // Use new gc-mfe-auth when embedded (not standalone)
    if (!isStandalone) {
        sessionService.isLoggedIn(true);
        sessionService.isSessionValid(true);
        serverLogger.isLoggedIn(true);
        return;
    }

    // Legacy behavior: get token from localStorage (when standalone)
    let oAuthToken;

    try {
        oAuthToken = getPcAuthToken();

        if (oAuthToken.secure.token_type !== "bearer") {
            throw new Error("No OAuth token set.");
        }
    } catch (err) {
        localLogger.error("Unable to load OAuth credentials", err);
        oAuthError(true);
        return;
    }

    apiCore.setOAuthBearerToken(oAuthToken.secure.access_token);
    uploadService.setOauthBearerToken(oAuthToken.secure.access_token);

    sessionService.isLoggedIn(true);
    sessionService.isSessionValid(true);
    serverLogger.isLoggedIn(true);
}

const init = function (store) {
    store.subscribe(() => {
        const state = store.getState();
        if (selectStatus(state) === "finished") {
            registerPermissions(selectAuthorization(state));
            principalId(selectUser(state)?.id);
        }
    });
    return registerOauthSession();
};

const deprecationWarnings = function (permissionData) {
    const deprecationWarning =
        permissionData.warn || _.find(permissionData, { warn: true });

    if (deprecationWarning) {
        logDeprecationMessage(deprecationWarning.permission);
    }
};

/**
 * Authentication service. Handles user login,
 * storing information about the logged in user
 * and the user's authenticaiton token.
 * @exports services/authService
 */
const authService = {
    init: init,

    /**
     * Get a fresh authentication token.
     * Always tries to fetch from gc-mfe-auth first (new behavior).
     * Falls back to legacy localStorage if gc-mfe-auth fails or unavailable.
     *
     * @returns {Promise<string>} The current bearer token
     */
    getToken: async function () {
        const isStandalone = window === window.parent;

        // Always try gc-mfe-auth first when embedded (not standalone)
        if (!isStandalone) {
            try {
                const token = await authClientService.getToken();
                if (token) {
                    return token;
                }
            } catch (err) {
                localLogger.warn(
                    "Failed to fetch token from gc-mfe-auth, falling back to legacy",
                    err
                );
                // Continue to fallback below
            }
        }

        // Fallback: return cached token from localStorage (when standalone or gc-mfe-auth failed)
        try {
            const oAuthToken = getPcAuthToken();
            return Promise.resolve(oAuthToken.secure.access_token);
        } catch (err) {
            localLogger.error(
                "Unable to get OAuth token from localStorage",
                err
            );
            return Promise.reject(err);
        }
    },

    /**
     * Flag indicating principalId has been set (indicating that both authorizations and user id are ready for appropriate permissions checking)
     *
     * @returns {boolean} Ensures that principalId has a truthy value
     */
    isLoggedIn: ko.computed(function () {
        return !!principalId();
    }),

    /**
     * Get the user id for the currently logged in user.
     *
     * @returns {observable} obervable for the logged in user's id.
     */
    principalId: ko.computed(function () {
        return principalId();
    }),

    /**
     * Checks to see if a user has a given authorization.
     *
     * @param {object} authorizations      The authorizations to check for
     * @param {array} authorizations.any                Permissions that the user must have at least one of (OR)
     * @param {array} authorizations.all                Permissions that the user must have all of (AND)
     *
     * @returns {boolean} true if the user has the authorization, false otherwise
     */
    hasAuthorization: function (authorizations) {
        const getAuths = function (auths) {
            const permissionsData = _.map(
                auths,
                getAuthorizationsAndPermissions
            );

            deprecationWarnings(permissionsData);

            return permissionsData;
        };

        let hasAny = false;
        let hasAll = false;
        let authData;

        // if 'any' is not defined or empty, hasAny will be set to true
        if (authorizations.any && !_.isEmpty(authorizations.any)) {
            authData = getAuths(authorizations.any);

            hasAny = _.some(authData, function (permData) {
                return _.some(permData.auths, function (perm) {
                    const trimmedAuth = trimDivisionFromPermission(perm);
                    const trimmedPerm = trimDivisionFromPermission(
                        permData.permission.toLowerCase()
                    );
                    return trimmedAuth === trimmedPerm;
                });
            });
        } else {
            hasAny = true;
        }

        // if 'all' is not defined or empty, hasAll will be set to true
        if (authorizations.all && !_.isEmpty(authorizations.all)) {
            authData = getAuths(authorizations.all);

            hasAll = _.every(authData, function (permData) {
                return _.some(permData.auths, function (perm) {
                    const trimmedAuth = trimDivisionFromPermission(perm);
                    const trimmedPerm = trimDivisionFromPermission(
                        permData.permission.toLowerCase()
                    );
                    return trimmedAuth === trimmedPerm;
                });
            });
        } else {
            hasAll = true;
        }

        // every set must pass entirely
        return hasAny && hasAll;
    },
};

export default authService;
